import { createSlice, PayloadAction } from "@reduxjs/toolkit"

// "search" backs the desktop inputs (searchSlice), "local" backs the mobile drawer (localSearchSlice).
export type LocationInputScope = "search" | "local"

// "empty" = Search with no destination, "no-match" = typed text has zero suggestions.
export type LocationInputStatus = "idle" | "resolving" | "empty" | "no-match"

export type LocationInputMethod =
  | "suggestion clicked"
  | "auto-resolved"
  | "none"

// Reset on every page change (LocationInputRouteReset), so none of this outlives its page.
type LocationInputScopeState = {
  // Raw text typed since the last committed place. null = field shows the committed place.
  pendingText: string | null
  status: LocationInputStatus
  // Page that raised the status; fields on any other page treat it as idle.
  statusPathname: string | null
  // How the current place got into the field, until the next search is submitted.
  committedMethod: LocationInputMethod
}

type LocationInputState = Record<LocationInputScope, LocationInputScopeState>

const initialScopeState: LocationInputScopeState = {
  pendingText: null,
  status: "idle",
  statusPathname: null,
  committedMethod: "none",
}

const initialState: LocationInputState = {
  search: { ...initialScopeState },
  local: { ...initialScopeState },
}

type RootState = {
  locationInputReducer: LocationInputState
}

const locationInputSlice = createSlice({
  name: "locationInput",
  initialState,
  reducers: {
    setPendingLocationText: (
      state,
      action: PayloadAction<{ scope: LocationInputScope; text: string }>,
    ) => {
      const { scope, text } = action.payload
      state[scope].pendingText = text
      state[scope].status = "idle"
      state[scope].statusPathname = null
      state[scope].committedMethod = "none"
    },
    markLocationCommitted: (
      state,
      action: PayloadAction<{
        scope: LocationInputScope
        method: LocationInputMethod
      }>,
    ) => {
      const { scope, method } = action.payload
      state[scope].pendingText = null
      state[scope].status = "idle"
      state[scope].statusPathname = null
      state[scope].committedMethod = method
    },
    resetLocationInput: (
      state,
      action: PayloadAction<{ scope: LocationInputScope }>,
    ) => {
      state[action.payload.scope] = { ...initialScopeState }
    },
    setLocationInputStatus: (
      state,
      action: PayloadAction<{
        scope: LocationInputScope
        status: LocationInputStatus
        pathname: string | null
      }>,
    ) => {
      const { scope, status, pathname } = action.payload
      state[scope].status = status
      state[scope].statusPathname = pathname
    },
  },
})

export const {
  setPendingLocationText,
  markLocationCommitted,
  resetLocationInput,
  setLocationInputStatus,
} = locationInputSlice.actions

export const selectLocationInput =
  (scope: LocationInputScope) => (state: RootState) =>
    state.locationInputReducer?.[scope] ?? initialScopeState

/** The field's status as seen on `pathname`: an error raised on another page is "idle". */
export const selectLocationStatus =
  (scope: LocationInputScope, pathname: string | null) =>
  (state: RootState): LocationInputStatus => {
    const input = selectLocationInput(scope)(state)
    return input.statusPathname === pathname ? input.status : "idle"
  }

export default locationInputSlice.reducer
